/**
 * startAnonymousSession - kicks off an anonymous "explore without signing up"
 * workspace and persists the resulting auth context to localStorage.
 *
 * Shared by the SignUpStep "explore" CTA, the dedicated /start route, and the
 * useOnboardingSession hook. The backend returns the same shape as verify-otp,
 * already at the `otp_verified` step, so callers can drop straight into the
 * path-choice screen of the wizard.
 */
import { buildApiUrl } from '../../config';
import { executeRecaptcha } from '../../utils/recaptcha';
import { setOnboardingTtvClock } from '../../utils/onboardingLocalStorage';
import { identifyUserFromOnboardingVerify } from '../../utils/analytics';
import { setAuth } from '../../utils/auth';

// Kept in sync with ONBOARDING_SESSION_STORAGE_KEY in useOnboardingSession.ts.
// Duplicated here (rather than imported) to avoid a circular module import.
const ONBOARDING_SESSION_STORAGE_KEY = 'klaris_onboarding_session_id';

export async function startAnonymousSession(opts?: { fresh?: boolean }): Promise<any> {
  if (opts?.fresh) {
    try {
      window.localStorage.removeItem('klaris_jwt');
      window.localStorage.removeItem('klaris_user');
      window.localStorage.removeItem('klaris_tenant');
      window.localStorage.removeItem(ONBOARDING_SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  const recaptcha_token = await executeRecaptcha('anonymous_start');
  const payload: Record<string, string> = {};
  if (recaptcha_token) payload.recaptcha_token = recaptcha_token;

  const resp = await fetch(buildApiUrl('/api/v1/onboarding/anonymous-start'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const detail = data?.detail;
    const message =
      typeof detail === 'string' ? detail : 'Could not start a session. Please try again.';
    throw new Error(message);
  }

  try {
    const u = data.user || {};
    const t = data.tenant || {};
    if (u.user_id && u.email && t.tenant_id && t.name) {
      identifyUserFromOnboardingVerify(
        {
          user_id: String(u.user_id),
          email: String(u.email),
          name: u.name ? String(u.name) : undefined,
          role_name: u.role_name ? String(u.role_name) : undefined,
        },
        { tenant_id: String(t.tenant_id), name: String(t.name) },
      );
    }
  } catch {
    /* analytics non-critical */
  }

  // notify:false — the wizard is still several steps from the app and calls
  // notifyAuthChange() itself at the handoff.
  setAuth(data.access_token, data.user, data.tenant, { notify: false });
  if (data.session_id) window.localStorage.setItem(ONBOARDING_SESSION_STORAGE_KEY, data.session_id);

  const tid =
    data.tenant && typeof data.tenant === 'object' && 'tenant_id' in data.tenant
      ? (data.tenant as { tenant_id?: string }).tenant_id
      : undefined;
  setOnboardingTtvClock(tid != null ? String(tid) : undefined);

  return data;
}
